import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@legal-500/legal500-design-system";
import { useSearchableSelect } from "../SearchableSelect/useSearchableSelect";
import type { SelectOption } from "../SearchableSelect/useSearchableSelect";
import selectStyles from "../SearchableSelect/SearchableSelect.module.css";
import styles from "./CreatableSelect.module.css";

export type CreatableSelectOption = {
  id: string;
  name: string;
};

/**
 * What the field holds. The two non-null shapes are the two things a caller can
 * do with the value — send an id, or send a name to be created — so the
 * distinction is carried on the value itself rather than recovered later by
 * checking whether the name happens to be in the options list.
 */
export type CreatableSelectValue =
  | { kind: "existing"; id: string; name: string }
  | { kind: "new"; name: string }
  | null;

// Mirrors awards-api's dictionary slug: a new name whose slug an option already
// has would not create a row but rename that option for every award using it.
function slugify(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type CreatableSelectProps = {
  name: string;
  label: string;
  required?: boolean;
  value: CreatableSelectValue;
  options: CreatableSelectOption[];
  onChange: (value: CreatableSelectValue) => void;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: string;
};

/**
 * A searchable single select that also accepts a value not in its options: when
 * the typed text matches no option by slug, the list ends with an
 * "Add “<text>”" row that selects it as a `{ kind: "new" }` value.
 *
 * Same chrome, positioning, click-outside and search filtering as
 * `SearchableSelect`, via the shared hook and stylesheet. Keyboard handling is
 * local, because the shared handler only knows how to walk the filtered options
 * and the add row has to be reachable by arrow keys too.
 */
export function CreatableSelect({
  name,
  label,
  required = false,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Search or add...",
  helperText,
  error,
}: CreatableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const rowRefs = useRef<Map<number, HTMLLIElement>>(new Map());

  const selectOptions = useMemo<SelectOption[]>(
    () => options.map(option => ({ value: option.id, label: option.name })),
    [options],
  );

  const {
    searchQuery,
    filteredOptions,
    dropdownStyle,
    containerRef,
    dropdownRef,
    searchInputRef,
    handleSearchChange,
  } = useSearchableSelect({
    options: selectOptions,
    isOpen,
    onClose: () => setIsOpen(false),
  });

  const typed = searchQuery.trim();
  const typedSlug = slugify(typed);
  const slugMatch =
    typed === "" ? undefined : selectOptions.find(option => slugify(option.label) === typedSlug);
  const rows =
    slugMatch && !filteredOptions.some(option => option.value === slugMatch.value)
      ? [...filteredOptions, slugMatch]
      : filteredOptions;
  const canAdd = typed !== "" && !slugMatch;
  const rowCount = rows.length + (canAdd ? 1 : 0);

  useEffect(() => {
    if (!isOpen) setHighlightedIndex(-1);
  }, [isOpen]);

  useEffect(() => {
    rowRefs.current.get(highlightedIndex)?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const selectExisting = (option: SelectOption) => {
    onChange({ kind: "existing", id: String(option.value), name: option.label });
    setIsOpen(false);
  };

  const selectNew = () => {
    onChange({ kind: "new", name: typed });
    setIsOpen(false);
  };

  const handleQueryChange = (query: string) => {
    handleSearchChange(query);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (rowCount > 0) setHighlightedIndex(prev => (prev < rowCount - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        if (rowCount > 0) setHighlightedIndex(prev => (prev > 0 ? prev - 1 : rowCount - 1));
        break;
      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < rows.length) {
          selectExisting(rows[highlightedIndex]);
        } else if (canAdd && highlightedIndex === rows.length) {
          selectNew();
        } else if (highlightedIndex < 0 && filteredOptions.length === 0) {
          // Bare Enter takes the typed text only when nothing else matches it.
          // With partial matches on screen, "apac" + Enter must not mint a new
          // dictionary row the server would keep for good.
          if (slugMatch) selectExisting(slugMatch);
          else if (canAdd) selectNew();
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const describedBy = error ? errorId : helperText ? helperId : undefined;
  const addRowIndex = rows.length;

  return (
    <div className={selectStyles.formField}>
      <label htmlFor={name} className={selectStyles.label}>
        {label}{" "}
        {required && (
          <span className={selectStyles.required} aria-label="required">
            *
          </span>
        )}
      </label>

      <div ref={containerRef} className={selectStyles.selectContainer}>
        <button
          type="button"
          id={name}
          className={selectStyles.selectButton}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-describedby={describedBy}
          aria-invalid={!!error}
        >
          {value ? (
            <span className={selectStyles.value}>
              {value.name}
              {value.kind === "new" && <span className={styles.newBadge}>New</span>}
            </span>
          ) : (
            <span className={selectStyles.placeholder}>{placeholder}</span>
          )}
          <span className={selectStyles.actions}>
            {value && !disabled && (
              <span
                className={selectStyles.clearButton}
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  onChange(null);
                }}
                role="button"
                aria-label="Clear selection"
              >
                <Icon icon="cross" size="small" />
              </span>
            )}
            <Icon
              icon={isOpen ? "chevron-up" : "chevron-down"}
              size="small"
              className={selectStyles.arrow}
            />
          </span>
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className={selectStyles.dropdown} style={dropdownStyle}>
            <div className={selectStyles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className={selectStyles.searchInput}
                placeholder="Search or type a new value..."
                aria-label={`Search ${label}`}
                value={searchQuery}
                onChange={event => handleQueryChange(event.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <ul className={selectStyles.optionsList} role="listbox">
              {rowCount === 0 && <li className={selectStyles.noResults}>No results found</li>}
              {rows.map((option, index) => {
                const isSelected = value?.kind === "existing" && value.id === option.value;
                return (
                  <li
                    key={String(option.value)}
                    ref={element => {
                      if (element) rowRefs.current.set(index, element);
                    }}
                    className={`${selectStyles.option} ${isSelected ? selectStyles.optionSelected : ""} ${
                      index === highlightedIndex ? selectStyles.optionHighlighted : ""
                    }`}
                    onClick={() => selectExisting(option)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className={selectStyles.optionContent}>
                      <span className={selectStyles.optionLabel}>{option.label}</span>
                    </div>
                  </li>
                );
              })}
              {canAdd && (
                <li
                  ref={element => {
                    if (element) rowRefs.current.set(addRowIndex, element);
                  }}
                  className={`${selectStyles.option} ${styles.addOption} ${
                    addRowIndex === highlightedIndex ? selectStyles.optionHighlighted : ""
                  }`}
                  onClick={selectNew}
                  role="option"
                  aria-selected={false}
                >
                  <div className={selectStyles.optionContent}>
                    <span className={selectStyles.optionLabel}>+ Add “{typed}”</span>
                  </div>
                </li>
              )}
            </ul>
          </div>,
          document.body,
        )}

      {error && (
        <span id={errorId} className={selectStyles.errorText} role="alert" aria-live="polite">
          {error}
        </span>
      )}
      {helperText && !error && (
        <span id={helperId} className={selectStyles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
}
